import React, { useEffect, useRef, useState } from 'react';
import {
  NavLink, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import { Breadcrumb, Button, Spin } from 'antd';
import {
  Article, useAddBookmark, useArticlesGet, useArticlesGetById,
  useRemoveBookmark,
} from '../../../hooks/api/article';

import styles from './index.module.scss';
import {
  IconBookmark, IconBookmarkCheck, IconFileText, IconPaperclip,
} from '../../Common/Icon';
import Questionarie from './Questionarie';

export default function ArticleContent(): React.ReactNode | null {
  const { name, id, artId } = useParams();
  const location = useLocation();
  const {
    subName, subId, catName, catId,
  } = location.state || {};
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [accumulatedArticles, setAccumulatedArticles] = useState<Article[]>([]);
  const articlesGetById = useArticlesGetById(artId);
  const { data, loading, error } = articlesGetById;
  const categoryIdParam = data?.category?.id ? [data.category.id] : undefined;
  const subCategoryIdParam = data?.subCategory?.id ? [data.subCategory.id] : undefined;
  const articlesGet = useArticlesGet({ autoStart: false });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addBookmark = useAddBookmark(artId);
  const removeBookmark = useRemoveBookmark(artId);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      console.log('Selected file:', file);
    }
  };

  const bookmarkClick = () => {
    if (data?.isBookmarkedByCurrentUser) {
      removeBookmark.fetch();
    } else {
      addBookmark.fetch();
    }
  };

  useEffect(() => {
    if (removeBookmark.data?.success || addBookmark.data) {
      articlesGetById.fetch();
    }
  }, [removeBookmark.data?.success, addBookmark.data]);

  useEffect(() => {
    if (data && !loading && !error) {
      articlesGet.fetch({
        categoryId: categoryIdParam,
        subCategoryId: subCategoryIdParam,
        page: String(page),
        pageSize: '5',
      });
    }
  }, [data, loading, error, page, removeBookmark.data?.success, addBookmark.data]);

  useEffect(() => {
    if (articlesGet.data?.data) {
      setAccumulatedArticles((prev) => [...prev, ...articlesGet.data!.data]);
    }
  }, [articlesGet.data]);

  return (
    <div key={artId}>
      {data
        ? (
          <div className="breadcrumb">
            <Breadcrumb
              items={[
                {
                  title: <NavLink to="/" className="breadcrumbLink">Help portal</NavLink>,
                },
                {
                  title: (
                    <NavLink
                      to={`/product/${data?.product?.id || ''}/${encodeURIComponent(data?.product?.name || '')}`}
                      className="breadcrumbLink"
                    >
                      {data?.product?.name}
                    </NavLink>
                  ),
                },
                {
                  title: (
                    <NavLink
                      // eslint-disable-next-line max-len
                      to={`/category/${encodeURIComponent(data?.product?.name || '')}/${data?.product?.id || ''}/${encodeURIComponent(data?.category?.name || '')}/${data?.category?.id}`}
                      className="breadcrumbLink"
                    >
                      {data?.category?.name}
                    </NavLink>
                  ),
                },
                // Если есть subCategory, добавь переход:
                ...(data?.subCategory ? [
                  {
                    title: (
                      <NavLink
                        // eslint-disable-next-line max-len
                        to={`/sub-category/${encodeURIComponent(data?.product?.name || '')}/${data?.product?.id || ''}/${encodeURIComponent(data?.category?.name || '')}/${data?.category?.id}/${encodeURIComponent(data?.subCategory?.name || '')}/${data?.subCategory?.id}`}
                        className="breadcrumbLink"
                      >
                        {data?.subCategory?.name}
                      </NavLink>
                    ),
                  },
                ] : []),
                { title: `${data?.title}` },
              ]}
            />

          </div>
        )
        : null}
      <div className={styles.articleContent}>
        <div className="wrapper">
          <div className={styles.content}>
            <h2 className={styles.title}>{data?.title}</h2>
            <div
              className="articleContent"
              dangerouslySetInnerHTML={{ __html: data?.content || '' }}
            />
            {data?.files.length
              ? (
                <div className={styles.files}>
                  <span>Attached files</span>
                  {data.files.map((item) => {
                    if (!item) return null;
                    const fileName = (item as string).split('/').pop() || 'download';

                    return (
                      <div
                        key={item}
                        className={styles.item}
                        onClick={() => {
                          // const link = document.createElement('a');

                          // link.href = item;
                          // link.download = fileName || 'download';
                          window.open(item, '_blank');
                          // link.click();
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className={styles.icon}>
                          <IconPaperclip />
                        </div>
                        <span>{fileName}</span>
                      </div>
                    );
                  })}
                </div>
              )
              : null}
            {data?.tags.length
              ? (
                <div>
                  <span>Tags</span>
                  <div className={styles.tags}>
                    {data.tags.map((item) => (
                      <div key={item}>
                        <Button
                          className={styles.tag}
                          type="text"
                          onClick={() => {
                            navigate(`/search-results?tags=${encodeURIComponent(item || '')}`);
                          }}
                        >
                          {item}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )
              : null}
            {data?.id === artId && !loading && <Questionarie id={data?.id} />}
            {data?.relatedArticles?.length
              ? (
                <div className={styles.relatedArticles}>
                  <h3>Related Articles</h3>
                  {data.relatedArticles.map((item) => (
                    <div
                      role="button"
                      tabIndex={0}
                      className={styles.item}
                      key={item.id}
                      onClick={() => {
                        navigate(`/article/${name}/${id}/${item.id}`);
                        window.location.reload();
                      }}
                    >
                      <div className={styles.icon}>
                        <IconFileText color="currentColor" />
                      </div>
                      {item.title}
                    </div>
                  ))}
                </div>
              )
              : null}
            {accumulatedArticles.length > 0 && (
              <div className={styles.relatedArticles}>
                <h3>Articles in this section</h3>
                {accumulatedArticles.map((item) => (
                  <div
                    role="button"
                    tabIndex={0}
                    className={styles.item}
                    key={item.id}
                    onClick={() => {
                      navigate(`/article/${name}/${id}/${item.id}`);
                      window.location.reload();
                    }}
                  >
                    <div className={styles.icon}>
                      <IconFileText color="currentColor" />
                    </div>
                    {item.title}
                  </div>
                ))}
                {articlesGet.data?.meta?.hasNextPage && (
                  <div>
                    <Button
                      type="link"
                      onClick={() => setPage((prev) => prev + 1)}
                      className={styles.showMore}
                      loading={articlesGet.loading}
                    >
                      Show more
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div>
          <Button
            type="primary"
            className={styles.bookmarkBtn}
            // icon={<IconBookmark color="#284F66" />}
            loading={removeBookmark.loading || addBookmark.loading}
            icon={data?.isBookmarkedByCurrentUser
              ? <IconBookmarkCheck color="#284F66" />
              : <IconBookmark color="#284F66" />}
            onClick={bookmarkClick}
          />
        </div>
        {loading ? (
          <div className="spin">
            <Spin />
          </div>
        ) : null}
      </div>
    </div>
  );
}
